"use client";
import { useState, useRef, useEffect } from "react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import {
  Send,
  Paperclip,
  Mic,
  Loader,
  Image,
  Loader2,
  X,
  Play,
  Square,
  Pause,
  ImageIcon,
  Video,
  File,
} from "lucide-react";
import { Avatar, Image as ImageAntd } from "antd";
import { BaseUrlApi, ErrorMessage, UrlImage } from "../../../lib/api";
import { cn, formatTime } from "../../../lib/utils";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "sonner";
import { Skeleton } from "src/components/ui/skeleton";
import { Progress } from "src/components/ui/progress";
import { AudioPlayer } from "./../../../components/common/AudioPlayer";
import CustomPlayer from "./../../../components/CustomPlayer";

export default function ChatInterface({
  selectedUser,
  conversation,
  messages,
  setMessages,
  loadingMessages,
}) {
  const { user } = useAuth();
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [uploadingVoice, setUploadingVoice] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [showRecordingControls, setShowRecordingControls] = useState(false);
  const [isPlaying, setIsPlaying] = useState({});
  const [uploadPreview, setUploadPreview] = useState(null);
  const [audioPreview, setAudioPreview] = useState(null);

  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const audioRefs = useRef({});
  const audioPreviewRef = useRef(null);

  const selectedUserData = selectedUser
    ? conversation.find((user) => user.id === selectedUser?.id)
    : null;

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-play audio preview when available
  useEffect(() => {
    if (audioPreview && audioPreviewRef.current) {
      audioPreviewRef.current.play();
    }
  }, [audioPreview]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorder.ondataavailable = (e) => chunks.push(e.data);
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/wav" });
        setAudioBlob(blob);

        // Create audio preview
        const audioUrl = URL.createObjectURL(blob);
        setAudioPreview(audioUrl);

        // Auto-send after recording stops
        // handleSubmitRecording(blob);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
      setShowRecordingControls(true);

      // Start timer
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => prev + 1);
      }, 1000);
    } catch (error) {
      toast.error("Error accessing microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream
        .getTracks()
        .forEach((track) => track.stop());
    }
    clearInterval(timerRef.current);
    setIsRecording(false);
  };

  const cancelRecording = () => {
    stopRecording();
    setAudioBlob(null);
    setShowRecordingControls(false);
    setRecordingTime(0);
    setAudioPreview(null);
  };

  const handleSubmitRecording = async (blob) => {
    const audioToUpload = blob || audioBlob;
    if (!audioToUpload) return;

    setUploadingVoice(true);

    const tempMessage = {
      id: Date.now(),
      content: "",
      sender: { id: user?.id },
      created_at: new Date().toISOString(),
      isLoading: true,
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      const formData = new FormData();
      formData.append("file", audioToUpload, "voice-message.wav");
      formData.append("name", "chats");

      const { data } = await axios.post(
        "https://pro1.edunezam.com/api/upload",
        formData,
        {
          onUploadProgress: (e) => {
            const progress = Math.round((e.loaded * 100) / e.total);
            setUploadProgress(progress);
          },
        }
      );

      const response = await axios.post(
        `${BaseUrlApi}/messages/${selectedUser?.pivot?.conversation_id}`,
        {
          sender_id: user?.id,
          content: "",
          file: data.url,
        }
      );

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempMessage.id ? response.data.data : msg
        )
      );
    } catch (error) {
      toast.error("Error uploading voice message");
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
    } finally {
      setUploadingVoice(false);
      setAudioBlob(null);
      setShowRecordingControls(false);
      setRecordingTime(0);
      setAudioPreview(null);
      setUploadProgress(0);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadPreview({
          type: "image",
          url: e.target.result,
          name: file.name,
          file,
        });
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith("video/")) {
      setUploadPreview({
        type: "video",
        name: file.name,
        file,
      });
    } else if (file.type.startsWith("audio/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadPreview({
          type: "audio",
          url: e.target.result,
          name: file.name,
          file,
        });
      };
      reader.readAsDataURL(file);
    } else {
      setUploadPreview({
        type: "file",
        name: file.name,
        file,
      });
    }

    setSelectedFile(file);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() && !selectedFile) return; // allow file-only send

    setIsLoading(true);
    setUploading(true);

    let fileUrl = uploadedFileUrl;

    // Upload file if needed
    if (selectedFile && !uploadedFileUrl) {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("name", "chats");

      try {
        const { data } = await axios.post(
          "https://pro1.edunezam.com/api/upload",
          formData,
          {
            onUploadProgress: (e) => {
              const progress = Math.round((e.loaded * 100) / e.total);
              setUploadProgress(progress);
            },
          }
        );
        fileUrl = data.url;
      } catch (err) {
        toast.error("Upload failed");
        setIsLoading(false);
        setUploading(false);
        return;
      }
    }

    // Send message
    try {
      const { data } = await axios.post(
        `${BaseUrlApi}/messages/${selectedUser?.pivot?.conversation_id}`,
        {
          sender_id: user?.id,
          content: inputValue || "", // allow empty message
          file: fileUrl || null,
        }
      );

      setMessages((prev) => [...prev, data.data]);
      setInputValue("");
      setSelectedFile(null);
      setUploadPreview(null);
      setUploadedFileUrl("");
    } catch (err) {
      toast.error(ErrorMessage(err));
    } finally {
      setIsLoading(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center p-4 border-b">
        {selectedUserData ? (
          <div className="flex items-center gap-2">
            <Avatar
              className={cn(
                "min-w-10 min-h-10 rounded-full flex items-center justify-center text-xs font-medium",
                "bg-blue-100 text-blue-600",
                "dark:bg-blue-900 dark:text-blue-300"
              )}
              src={`${UrlImage}${selectedUserData.image}`}
            >
              {selectedUserData.name.charAt(0)}
            </Avatar>
            <div>
              <h2 className="font-semibold">{selectedUserData.name}</h2>
              <p className="text-xs text-muted-foreground capitalize">
                {selectedUserData.status}
              </p>
            </div>
          </div>
        ) : (
          <Skeleton className="w-40 h-12" />
        )}
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 p-4 overflow-auto">
        <div className="space-y-4">
          {loadingMessages ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((_, i) => (
                <div
                  key={i}
                  className={`flex ${
                    i % 2 === 0 ? "justify-end" : "justify-start"
                  }`}
                >
                  <Skeleton
                    className={`h-14 w-1/4 rounded-2xl px-4 py-2 ${
                      i % 2 === 0 ? "rounded-br-none" : "rounded-bl-none"
                    }`}
                  />
                </div>
              ))}
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender.id !== selectedUser?.id
                    ? "justify-end"
                    : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                    message.sender.id !== selectedUser?.id
                      ? "bg-primary text-primary-foreground rounded-br-none"
                      : "bg-muted rounded-bl-none"
                  }`}
                >
                  {message.content && <p>{message.content}</p>}

                  {message.isLoading ? (
                    <div className="flex items-center gap-2 mt-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Uploading...</span>
                      {uploadProgress > 0 && (
                        <Progress
                          value={uploadProgress}
                          className="h-1 w-full mt-1"
                        />
                      )}
                    </div>
                  ) : (
                    message.file && (
                      <div className={message.content ? "mt-2" : ""}>
                        {message.file.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <div className="relative rounded-lg overflow-hidden">
                            <ImageAntd
                              src={`${UrlImage}${message.file}`}
                              alt="Message attachment"
                              className="rounded-lg object-cover max-w-[250px] max-h-[250px]"
                              preview={true}
                            />
                          </div>
                        ) : message.file.match(/\.(mp3|wav|webm|ogg)$/i) ? (
                          <AudioPlayer
                            id={`audio-${message.id}`}
                            audioUrl={`${UrlImage}${message.file}`}
                            isOutgoing={message.sender.id !== selectedUser?.id}
                          />
                        ) : message.file.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                          <CustomPlayer
                            sources={[
                              {
                                src: `${UrlImage}${message.file}`,
                                quality: "1080p",
                                type: "video/mp4",
                              },
                            ]}
                            title={message.sender.name}
                          />
                        ) : (
                          <div className="flex items-center gap-2 p-3 bg-background/10 rounded-lg max-w-xs">
                            <File className="h-5 w-5 shrink-0" />
                            <div className="flex-1 truncate text-sm">
                              {message.file.split("/").pop()}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() =>
                                window.open(
                                  `${UrlImage}${message.file}`,
                                  "_blank"
                                )
                              }
                              className="shrink-0"
                            >
                              Download
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  )}
                  <div
                    className={`text-xs mt-1 ${
                      message.sender.id !== selectedUser?.id
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground"
                    }`}
                  >
                    {formatTime(message.created_at)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Input Section */}
      <div className="p-4 border-t">
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          onChange={(e) => handleFileUpload(e.target.files[0])}
        />
        <input
          type="file"
          ref={imageInputRef}
          className="hidden"
          accept="image/*"
          onChange={(e) => handleFileUpload(e.target.files[0])}
        />
        <input
          type="file"
          ref={videoInputRef}
          className="hidden"
          accept="video/*"
          onChange={(e) => handleFileUpload(e.target.files[0])}
        />

        {/* Recording Controls */}
        {showRecordingControls && (
          <div className="mb-4 p-4 bg-primary/10 backdrop-blur-sm rounded-xl border border-primary/20 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isRecording ? (
                  <>
                    <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-sm font-medium">
                      {Math.floor(recordingTime / 60)}:
                      {String(recordingTime % 60).padStart(2, "0")}
                    </span>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <Mic className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">
                      Voice message ready
                    </span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={cancelRecording}
                  className="text-red-500 hover:text-red-600"
                >
                  Cancel
                </Button>
                {!isRecording && audioBlob && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => handleSubmitRecording()}
                    disabled={uploadingVoice}
                  >
                    {uploadingVoice ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Send
                  </Button>
                )}
              </div>
            </div>
            {isRecording ? (
              <div className="mt-3">
                <Progress value={(recordingTime % 3) * 33.33} className="h-1" />
              </div>
            ) : (
              audioPreview && (
                <div className="mt-3 flex items-center gap-2 bg-background/30 p-2 rounded-lg">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 rounded-full bg-primary/20"
                    onClick={() => {
                      if (audioPreviewRef.current) {
                        if (audioPreviewRef.current.paused) {
                          audioPreviewRef.current.play();
                        } else {
                          audioPreviewRef.current.pause();
                        }
                      }
                    }}
                  >
                    {audioPreviewRef.current?.paused ? (
                      <Play className="h-4 w-4" />
                    ) : (
                      <Pause className="h-4 w-4" />
                    )}
                  </Button>
                  <div className="flex-1">
                    <Progress
                      value={
                        audioPreviewRef.current
                          ? (audioPreviewRef.current.currentTime /
                              audioPreviewRef.current.duration) *
                            100
                          : 0
                      }
                      className="h-1"
                    />
                  </div>
                  <audio
                    ref={audioPreviewRef}
                    src={audioPreview}
                    className="hidden"
                    onTimeUpdate={() => {
                      // Force re-render to update progress
                      setShowRecordingControls(true);
                    }}
                    onEnded={() => {
                      // Reset when done playing
                      if (audioPreviewRef.current) {
                        audioPreviewRef.current.currentTime = 0;
                      }
                    }}
                  />
                </div>
              )
            )}
          </div>
        )}

        {/* Upload Preview */}
        {uploadPreview && (
          <div className="mb-4 p-3 bg-primary/5 rounded-lg border border-primary/10 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {uploadPreview.type === "image" ? (
                  <div className="h-12 w-12 rounded overflow-hidden border">
                    <img
                      src={uploadPreview.url || "/placeholder.svg"}
                      alt="Preview"
                      className="h-full w-full object-cover"
                    />
                  </div>
                ) : uploadPreview.type === "video" ? (
                  <div className="flex items-center justify-center h-12 w-12 bg-primary/10 rounded">
                    <Video className="h-6 w-6 text-primary" />
                  </div>
                ) : uploadPreview.type === "audio" ? (
                  <div className="flex items-center justify-center h-12 w-12 bg-primary/10 rounded">
                    <Mic className="h-6 w-6 text-primary" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-12 w-12 bg-primary/10 rounded">
                    <File className="h-6 w-6 text-primary" />
                  </div>
                )}
                <div className="flex flex-col">
                  <span className="text-sm font-medium truncate max-w-[180px]">
                    {uploadPreview.name}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {uploadPreview.file.size < 1024 * 1024
                      ? `${Math.round(uploadPreview.file.size / 1024)} KB`
                      : `${
                          Math.round(
                            (uploadPreview.file.size / (1024 * 1024)) * 10
                          ) / 10
                        } MB`}
                  </span>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setUploadPreview(null);
                  setSelectedFile(null);
                  setUploadedFileUrl("");
                }}
                className="text-red-500 hover:text-red-600"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Progress Bar */}
            {uploading && uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-2">
                <div className="flex justify-between text-xs mb-1">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <Progress value={uploadProgress} className="h-1" />
              </div>
            )}
          </div>
        )}

        {/* Input Controls */}
        <div className="flex items-center gap-2">
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              className="rounded-full"
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading || uploading || isRecording || uploadingVoice}
            >
              <Paperclip className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full"
              onClick={() => imageInputRef.current?.click()}
              disabled={isLoading || uploading || isRecording || uploadingVoice}
            >
              <ImageIcon className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full"
              onClick={() => videoInputRef.current?.click()}
              disabled={isLoading || uploading || isRecording || uploadingVoice}
            >
              <Video className="h-5 w-5" />
            </Button>
          </div>
          <Button
            variant="outline"
            size="icon"
            className={cn(
              "rounded-full transition-colors",
              isRecording && "bg-red-500 text-white hover:bg-red-600"
            )}
            onClick={isRecording ? stopRecording : startRecording}
            disabled={uploadingVoice || isLoading || uploading}
          >
            {isRecording ? (
              <Square className="h-5 w-5" />
            ) : (
              <Mic className="h-5 w-5" />
            )}
          </Button>
          <div className="flex-1 relative">
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type a message..."
              className="pe-10 rounded-full"
              disabled={isLoading || uploading || isRecording || uploadingVoice}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
            />
          </div>
          <Button
            onClick={() => handleSendMessage()}
            size="icon"
            className="rounded-full"
            disabled={
              (!inputValue.trim() && !selectedFile) ||
              isLoading ||
              uploading ||
              isRecording ||
              uploadingVoice
            }
          >
            {isLoading ? (
              <Loader2 className="animate-spin h-5 w-5" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
